import {Injectable, OnDestroy} from '@angular/core';
import {SharedkeyDataService} from '@service/services/common-services/sharedkey-data.service';
import {CustomerConsentModel, KeyIdentifierModel} from '@service/models';
import {FinanceService} from '@service/services/server/finance.service';
import {ApplicationService, OfferService} from '@service/services';
import {environment} from '@env/environment';
import {AppConfigService} from '@service/config/app-config.service';
import {lastValueFrom, throwError, Subscription} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {DepositFinalConsentModalComponent} from '@core/_shared/shared-components/deposit-final-consent-modal/deposit-final-consent-modal.component';
import {ConsentEnum, Event} from '@service/enum/consent-enum';
import {SharedService} from '@core/util-services/shared.service';
import {
    MissingFieldComponent,
    MissingFieldDialogResult
} from '@shared/form-components/missing-field/missing-field.component';

@Injectable({
    providedIn: 'root'
})
export class CommonFinanceService implements OnDestroy {

    private keyIdentifier: KeyIdentifierModel;
    private subscription = new Subscription();
    appConfig: any;
    isYouthAccount: boolean;

    constructor(private sharedKeyDataService: SharedkeyDataService,
                private financeService: FinanceService,
                private applicationService: ApplicationService,
                private offerService: OfferService,
                private dialog: MatDialog,
                private router: Router,
                private sharedService: SharedService) {
        this.appConfig = AppConfigService.config;
        this.subscription.add(
            this.sharedKeyDataService.keyIdentifier.subscribe(data => {
                this.keyIdentifier = data;
            })
        );
    }

    finalSubmit(finance) {
        return new Promise((resolve, reject) => {
            this.financeService.oldFinanceSubmit(this.keyIdentifier.applicationId, finance)
                .subscribe(async (res: any) => {
                    // server rejected the submit for missing mandatory fields, nothing was saved or sent
                    if (MissingFieldComponent.isMissingFieldsResponse(res)) {
                        const result = await this.collectMissingFields(res, finance);
                        if (!result.response) {
                            // re-submit failed, or cancelled: the application did not advance
                            reject(result.error || result);
                            return;
                        }
                        res = result.response;
                    }
                    if (res.finalSubmitWebForwardLinkCredential) {
                        const credentials = {
                            username: res.finalSubmitWebForwardLinkUserName,
                            password: res.finalSubmitWebForwardLinkCredential,
                            link: res.finalSubmitWebForwardLink,
                            lendername: finance.lenderName
                        };

                        this.financeService.finalSummitCredentials.next(credentials);
                    }
                    this.keyIdentifier.applicationStatus = res.applicationStatus;
                    this.keyIdentifier.losStatus = res.losStatus;
                    // Finance submit call give losStatus same as underwriting status so we can avoid get application status call
                    this.keyIdentifier.underWritingStatus = res.losStatus;
                    this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);
                    resolve(res);

                }, error => {
                    reject(error);
                });
        });
    }

    /**
     * Opens the missing-field modal for a 6xxx final submit response. It saves the collected values and re-submits
     * the unchanged finance payload, resolving with the successful response, the re-submit error, or cancelled.
     */
    collectMissingFields(res: any, finance): Promise<MissingFieldDialogResult> {
        const applicants = (this.keyIdentifier.applicants && this.keyIdentifier.applicants.length)
            ? this.keyIdentifier.applicants
            : [this.keyIdentifier.primaryApplicant].filter(a => !!a);
        return lastValueFrom(
            this.dialog.open(MissingFieldComponent, {
                disableClose: true,
                width: '450px',
                data: {
                    missingFieldApplicants: res.missingFieldApplicants,
                    applicants,
                    resubmit: () => this.financeService.oldFinanceSubmit(this.keyIdentifier.applicationId, finance)
                }
            }).afterClosed()
        ).then(result => result || {cancelled: true});
    }

    getPaymentUrl(paymentInfo) {
        return new Promise((resolve, reject) => {
            let clientAppNumber: any = null;
            if (this.keyIdentifier.clientApplicationNumber) {
                try {
                    clientAppNumber = JSON.parse(this.keyIdentifier.clientApplicationNumber);
                } catch (e) {
                    console.log(e);
                }
            }

            // do not remove %26 in returnUrl and CancelURL, it is required for authorize.net
            const paymentData = {
                applicationId: this.keyIdentifier.applicationId,
                accountNumber: (typeof (clientAppNumber === 'object') && clientAppNumber && clientAppNumber.memberId) ? clientAppNumber.memberId : this.keyIdentifier.applicationId,
                amount: (paymentInfo.amountFinance) ? Number((paymentInfo.amountFinance.toString()).replace(/[^0-9.-]+/g, '')) : 0,
                paymentMethod: paymentInfo.paymentMethod,
                hostUrl: `https://${window.location.host}/assets/iframeCommunicator.html`,
                returnUrl: `${window.location.protocol}//${window.location.host}/redirect?appId=${this.keyIdentifier.applicationId}%26feature=payment%26event=success`,
                cancelUrl: `${window.location.protocol}//${window.location.host}/redirect?appId=${this.keyIdentifier.applicationId}%26feature=payment%26event=cancel`,
                productLevelPaymentDetailsVOS: paymentInfo.productLevelPaymentDetailsVOS,
            };

            this.offerService.getPaymentLink(paymentData)
                .subscribe((res: any) => {
                    resolve(res);
                }, err => {
                    this.sharedKeyDataService.mapErrorCodes(err, this.keyIdentifier);
                    reject(err);
                });
        });
    }

    async getConsentHTMLFromS3(isCoApplicant = false): Promise<string> {
        const defaultFileName = 'final-consent.html';
        let fileName = defaultFileName;
        this.isYouthAccount = this.keyIdentifier.loanPurposeConfig.title.toLowerCase().includes('youth');

        if (this.keyIdentifier.loanPurposeConfig && this.isYouthAccount) {
            if(isCoApplicant){
                fileName = 'parent-final-consent.html';
            }
            else {
                fileName = 'youth-final-consent.html';
            }
        } else {
            const citizenshipPrefix = this.getCitizenshipPrefix(isCoApplicant);

            if (this.appConfig.citizenshipBasedConsent) {
                if (this.appConfig.productBasedConsent) {
                    fileName = `${citizenshipPrefix}-${this.keyIdentifier.loanPurposeConfig.id}-final-consent.html`;
                } else if (this.keyIdentifier.subProductName) {
                    fileName = `${this.keyIdentifier.subProductName}-final-consent.html`;  // no prefix
                } else {
                    fileName = `${citizenshipPrefix}-final-consent.html`;
                }
            } else if (this.appConfig.productBasedConsent) {
                fileName = `${this.keyIdentifier.loanPurposeConfig.id}-final-consent.html`;
            }
        }

        try {
            return await this.getFileFromS3(fileName);
        } catch {
            return await this.getFileFromS3(defaultFileName);
        }
    }

    private getCitizenshipPrefix(isCoApplicant: boolean): string {
        const applicant = this.keyIdentifier.applicants?.find(item => item.applicantType === (isCoApplicant ? 'S' : 'P'));
        switch (applicant?.citizenshipStatusDesc) {
            case 'CITIZEN':
                return 'citizen';
            case 'PERMANENTRESIDENT':
                return 'permanentresident';
            default:
                return 'nonresident';
        }
    }

    getFileFromS3(fileName): Promise<string> {
        return lastValueFrom(
            this.financeService.getFileContent(`${environment.s3Url}html/${environment.institutionId}/${fileName}`)
                .pipe(
                    map(res => res.toString()),
                    catchError(err => throwError(err))
                )
        );
    }

    checkFraud(coBoFlow = false) {
        return new Promise((resolve, reject) => {
            this.applicationService.putFraud(this.keyIdentifier.applicationId, coBoFlow).subscribe(response => {
                console.log('fraud Reposne =======', response);
                if (response.code === 200) {
                    resolve(true);
                } else {
                    reject(false);
                }
            });
        });
    }

    /**
     * Opens the co-applicant's final deposit consent modal, records the e-consent, runs the
     * fraud check and navigates on. Shared by the co-applicant flow and the co-applicant
     * additional-features step, since either one may be the last screen before provisioning.
     * Resolves false only when the member cancels the modal (caller re-enables its submit button);
     * every other outcome, including a failed fraud check, navigates away on its own.
     */
    submitCoApplicantFinalConsent(): Promise<boolean> {
        return new Promise((resolve) => {
            this.dialog.open(DepositFinalConsentModalComponent, {
                disableClose: true,
                hasBackdrop: true,
                width: '600px',
                autoFocus: '#deposit-final-consent-dialog-title',
                restoreFocus: true,
                ariaLabelledBy: 'deposit-final-consent-dialog-title',
                data: {
                    keyIdentifier: this.keyIdentifier,
                    showLogo: 'no',
                    isCoApplicant: true
                }
            }).afterClosed().subscribe(async (res) => {
                if (!res) {
                    resolve(false);
                    return;
                }

                const body = {
                    docName: 'FinalConsent',
                    docContent: res
                };
                await lastValueFrom(this.applicationService.postHtmlToPdfCoApplicant(this.keyIdentifier.applicationId, body));

                const consent = new CustomerConsentModel();
                consent.name = ConsentEnum.DepositElectronicComm;
                consent.value = 'Y';
                consent.consentText = res || '';

                this.applicationService.addCoApplicantConsent(this.keyIdentifier.applicationId, [consent]).subscribe((consentResp) => {
                    this.keyIdentifier.applicationStatus = consentResp.applicationStatus;
                    this.keyIdentifier.isCoBoConsentFlow = true;
                    this.sharedKeyDataService.announceIdentifierChanges(this.keyIdentifier);

                    this.checkFraud(true).then(() => {
                        this.router.navigate(['/provision']);
                        resolve(true);
                    }, () => {
                        console.log('Error on Fraud Call');
                        this.sharedService.showNotification('Something went wrong in checking fraud status', Event.Error);
                        this.router.navigate(['thankyou']);
                        resolve(true);
                    });
                });
            });
        });
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
}
